import { Component } from 'react';
import { withRouter } from 'react-router-dom';
import loginService from '../../services/login';
import { getCookie, setCookie } from '../../services/axiosInstance';
import UserContext from '../context/userContext';

/**
 * RouteGuard component that validates session on every route change
 * Integrates with the existing login middleware
 */
class RouteGuard extends Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    this.hasValidatedOnMount = false;
  }

  componentDidMount() {
    // Validate session on initial mount
    this.validateSessionOnRouteChange();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const profile = this.context;
    
    // Check if route has changed
    if (prevProps.location.pathname !== this.props.location.pathname) {
      console.log('[RouteGuard] Route changed from', prevProps.location.pathname, 'to', this.props.location.pathname);
      this.validateSessionOnRouteChange();
      return;
    }

    // Check if context became ready (permissions loaded) and we haven't validated yet
    if (!this.hasValidatedOnMount && profile && profile.permissions && profile.permissions.length > 0) {
      console.log('[RouteGuard] Context became ready, validating session');
      this.hasValidatedOnMount = true;
      this.validateSessionOnRouteChange();
    }
  }

  validateSessionOnRouteChange = async () => {
    const profile = this.context;
    
    // Check if this is a public page (same logic as in App.js)
    const isPublicPage = window.location.href.includes("/public");
    
    // Only validate for authenticated pages (pages that require login)
    // Skip validation for public pages, if no token exists, or if context is not ready
    // Note: 'token' cookie is httpOnly, so we use 'token_js' instead
    const tokenJs = getCookie('token_js');
    const cookieToken = getCookie('token');
    const localToken = localStorage.getItem('token');
    const token = tokenJs || cookieToken || localToken;
    
    console.log('[RouteGuard] Token sources:', {
      tokenJs: tokenJs ? '✅ Found (readable)' : '❌ Not found',
      cookieToken: cookieToken ? '✅ Found (httpOnly - not readable)' : '❌ Not found',
      localToken: localToken ? '✅ Found' : '❌ Not found'
    });
    
    console.log('[RouteGuard] Validation check:', {
      hasToken: !!token,
      tokenValue: token ? `${token.substring(0, 10)}...` : 'none',
      hasProfile: !!profile,
      isPublicPage,
      hasPermissions: !!(profile && profile.permissions),
      permissionsCount: profile?.permissions?.length || 0
    });
    
    if (!token) {
      console.log('[RouteGuard] No token found');
      
      // Check if there are other session cookies (PHP session still active)
      const hasPhpSession = getCookie('member_name') || getCookie('session_token');
      
      if (hasPhpSession && profile.permissions && profile.permissions.length > 0) {
        // PHP session is active but no React token
        // This is expected until backend creates token cookie on login
        console.warn('[RouteGuard] ⚠️ PHP session exists but no token cookie - backend should create "token" cookie on login');
        console.warn('[RouteGuard] ℹ️ Session validation skipped - waiting for token-based authentication implementation');
        return;
      }
      
      console.log('[RouteGuard] Skipping validation: No token and no active session');
      return;
    }
    
    if (!profile) {
      console.log('[RouteGuard] Skipping validation: No profile context');
      return;
    }
    
    if (isPublicPage) {
      console.log('[RouteGuard] Skipping validation: Public page');
      return;
    }
    
    if (!profile.permissions || profile.permissions.length === 0) {
      console.log('[RouteGuard] Skipping validation: Permissions not loaded yet');
      return;
    }

    console.log('[RouteGuard] Starting session validation...');
    
    try {
      // Use the login middleware to validate session
      const data = await loginService.validateSession();
      
      console.log('[RouteGuard] Validation response:', { valid: data?.valid, hasToken: !!data?.token });
      
      if (!data || !data.valid || !data.token) {
        // Session is invalid - just log warning, don't force logout
        console.warn('[RouteGuard] ⚠️ Session validation failed on route change');
        console.warn('[RouteGuard] ℹ️ Token may be invalid or expired - user should re-login manually');
        return;
      }
      
      console.log('[RouteGuard] Session is valid');

      // Session is valid, update cookies from response
      if (data.cookies) {
        const cookiesData = data.cookies;
        
        // Check if important cookies have changed
        const currentMemberIdempresa = getCookie('member_idempresa');
        const currentMemberType = getCookie('member_type');
        const currentMemberIdemployee = getCookie('member_idemployee');
        
        const cookiesChanged = (
          (cookiesData.member_idempresa && cookiesData.member_idempresa !== currentMemberIdempresa) ||
          (cookiesData.member_type && cookiesData.member_type !== currentMemberType) ||
          (cookiesData.member_idemployee && cookiesData.member_idemployee !== currentMemberIdemployee)
        );
        
        // Set all cookies from response
        if (cookiesData.token) setCookie('token', cookiesData.token, 1);
        if (cookiesData.token_js) setCookie('token_js', cookiesData.token_js, 1);
        if (cookiesData.ePOS) setCookie('ePOS', cookiesData.ePOS, 1);
        if (cookiesData.idempresa) setCookie('idempresa', cookiesData.idempresa, 1);
        if (cookiesData.session_token) setCookie('session_token', cookiesData.session_token, 1);
        
        // Set all member_* cookies
        if (cookiesData.member_idempresa) setCookie('member_idempresa', cookiesData.member_idempresa, 1);
        if (cookiesData.member_idemployee) setCookie('member_idemployee', cookiesData.member_idemployee, 1);
        if (cookiesData.member_type) setCookie('member_type', cookiesData.member_type, 1);
        if (cookiesData.member_name) setCookie('member_name', cookiesData.member_name, 1);
        if (cookiesData.member_rname) setCookie('member_rname', cookiesData.member_rname, 1);
        if (cookiesData.member_avatar) setCookie('member_avatar', cookiesData.member_avatar, 1);
        if (cookiesData.member_nif) setCookie('member_nif', cookiesData.member_nif, 1);
        if (cookiesData.combustivel) setCookie('combustivel', cookiesData.combustivel, 1);
        
        // Update localStorage as well for backward compatibility
        if (data.token) localStorage.setItem('token', data.token);
        if (data.idempresa) localStorage.setItem('public_indentifier', data.idempresa);
        if (data.avatar) localStorage.setItem('avatar', data.avatar);
        
        // Only reload if important cookies changed
        if (cookiesChanged) {
          console.log('[RouteGuard] ✅ Important cookies changed - reloading page');
          window.location.reload();
        } else {
          console.log('[RouteGuard] ✅ Cookies updated (no reload needed)');
        }
      }
      
    } catch (error) {
      // If validation fails (network error, 401, etc.), just log warning
      console.error('[RouteGuard] ⚠️ Session validation error on route change:', error);
      console.warn('[RouteGuard] ℹ️ Unable to validate session - user may need to re-login manually');
    }
  };

  render() {
    // This component doesn't render anything, it just provides side effects
    return this.props.children;
  }
}

export default withRouter(RouteGuard);
